import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import ConfirmationPopup, {
  DIALOG_TYPES,
} from "../../../packages/ConfirmationPopup";
import CustomModal from "../../../packages/CustomModal";
import WPMessageBanner, {
  BANNER_TYPES,
} from "../../../packages/WPMessageBanner/WPMessageBanner";
import WPTertiaryButton from "../../../packages/WPTertiaryButton";

import createNotification, {
  ERROR_NOTIFICATION,
  SUCCESS_NOTIFICATION,
} from "../../Notifications";
import ChemicalLibraryAddEditDialog from "../ChemicalLibraryAddEditDialog/ChemicalLibraryAddEditDialog";
import ChemicalLibraryDeleteDialog from "../ChemicalLibraryDeleteDialog";
import ChemicalLibraryTable from "../ChemicalLibraryTable/ChemicalLibraryTable";
import {
  checkChemicalUsageData,
  checkUserChemicalUsage,
  deleteChemicalLibraryData,
  fetchChemicalLibraryData,
  updateChemicalLibraryData,
} from "./ChemicalLibraryDialog.api";

import {
  ALLOWED_CHEMICAL_CATEGORIES,
  CustomModalButtons,
  FEATURE_SCOPE,
  PENDING_PLANT_CHEMICAL_LIBRARY_KEY,
  SESSION_STORAGE_KEYS,
} from "../../../constants/global.constants";

import { NewBadgeIcon } from "../../../assets/icons";

import IconWithTooltip from "../../../common/styles/components/headings/IconWithTooltip";

import { getItemSession, saveItemSession } from "../../../utils/session";
import { extractPlantId, getPlantIdFromCurrentUrl } from "../../../utils/Utils";

import {
  storePlantChemicalLibrary,
  storeUserChemicalLibrary,
} from "../../../Store/TemplateStore/template.action";

import "./ChemicalLibraryDialog.scss";

const { PENDING_USER_CHEMICALS_TO_BE_ADDED } = SESSION_STORAGE_KEYS;

const ChemicalLibraryDialog = ({ onClose, scope = FEATURE_SCOPE.USER }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isUserScope = scope === FEATURE_SCOPE.USER;

  const [chemicalLibrary, setChemicalLibrary] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("");
  const [currentAction, setCurrentAction] = useState(null);
  const [showAddEditDialog, setShowAddEditDialog] = useState(false);
  const [pendingChemicalData, setPendingChemicalData] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  // Refer to ChemicalVisibilityToggleRenderer for details
  // const [showToggleWarning, setShowToggleWarning] = useState(false);
  const [showEditUsageWarning, setShowEditUsageWarning] = useState(false);
  const [shouldResetData, setShouldResetData] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [pendingDeleteChemical, setPendingDeleteChemical] = useState(null);
  const [pendingDeleteUsageList, setPendingDeleteUsageList] = useState([]);
  // Refer to ChemicalVisibilityToggleRenderer for details
  // const [pendingToggleChemical, setPendingToggleChemical] = useState(null);
  const [pendingEditChemical, setPendingEditChemical] = useState(null);

  const { userChemicalLibrary, plantChemicalLibrary } = useSelector(
    (state) => state,
  );

  const sortDefaultFirst = (list = []) =>
    [...(Array.isArray(list) ? list : [])].sort(
      (a, b) => Number(Boolean(a?.is_custom)) - Number(Boolean(b?.is_custom)),
    );

  const filteredAndSortedChemLibrary = useMemo(() => {
    const filteredChemLibrary = chemicalLibrary.filter((chem) =>
      ALLOWED_CHEMICAL_CATEGORIES.includes(chem.category),
    );
    const sortedChemLibrary = sortDefaultFirst(filteredChemLibrary);

    return sortedChemLibrary;
  }, [chemicalLibrary]);

  const makeDefaultBtn = (
    <div className="info-section">
      <IconWithTooltip label={t("chemicalLibrary.makeDefaultInfo")} />
      <WPTertiaryButton
        label={t("chemicalLibrary.makeDefaultLabel")}
        onClick={() => setShowConfirmation(true)}
        disabled={hasUnsavedChanges}
        variant="text"
        size="small"
        className="chemical-lib-make-default-btn"
      />
    </div>
  );

  const isMakeDefaultConfirmation = !isUserScope && !hasUnsavedChanges;

  const plantId = extractPlantId();
  const isLocalChanges = !isUserScope && !isMakeDefaultConfirmation && !plantId;

  const mergeChangedChemicalRows = (existingRows = [], updatedRows = []) => {
    const changedRows = Array.isArray(updatedRows) ? updatedRows : [];

    return existingRows.map((item) => {
      const changedItem = changedRows.find((row) => {
        const keyToMatch = row?.tempChemicalId ? "tempChemicalId" : "chem_id";
        const rowValue = row?.[keyToMatch];
        const itemValue = item?.[keyToMatch];

        return Boolean(rowValue) && rowValue === itemValue;
      });

      return changedItem ? { ...item, ...changedItem } : item;
    });
  };

  const hasDuplicateSymbolAndConcentration = (
    existingRows = [],
    updatedRows = [],
  ) => {
    return updatedRows.some((changedItem) => {
      return existingRows.some((existingItem) => {
        const isSameChemicalBeingChanged =
          existingItem?.tempChemicalId === changedItem?.tempChemicalId;

        // If a chemical is being changed, no need to check it against itself for duplicate symbol and concentration.
        if (isSameChemicalBeingChanged) {
          return false;
        }

        return (
          existingItem?.symbol === changedItem?.symbol &&
          Number(existingItem?.bulk_concentration) ===
            Number(changedItem?.bulk_concentration)
        );
      });
    });
  };

  const getChemicalsFromSession = (sessionKey) => {
    try {
      const data = JSON.parse(getItemSession(sessionKey) || "[]");
      return Array.isArray(data) ? data : [];
    } catch {
      return [];
    }
  };

  const mergeWithPendingChemicals = (fetchedLibrary = []) => {
    if (!Array.isArray(fetchedLibrary)) {
      return [];
    }

    let pendingUserChemicalsToBeAdded = getChemicalsFromSession(
      PENDING_USER_CHEMICALS_TO_BE_ADDED,
    );
    const pendingPlantChemicalData =
      getChemicalsFromSession(PENDING_PLANT_CHEMICAL_LIBRARY_KEY)?.chemicals ||
      [];

    if (
      !Array.isArray(pendingUserChemicalsToBeAdded) ||
      pendingUserChemicalsToBeAdded.length === 0
    ) {
      return fetchedLibrary;
    }

    // if user already edited some pending custom plant chemicals, retain them
    if (!isUserScope && pendingPlantChemicalData.length > 0) {
      pendingUserChemicalsToBeAdded = pendingUserChemicalsToBeAdded.filter(
        (pendingUserChem) =>
          !pendingPlantChemicalData.some(
            (pendingPlantChem) =>
              pendingPlantChem?.tempChemicalId ===
              pendingUserChem?.tempChemicalId,
          ),
      );
    }

    return [...fetchedLibrary, ...pendingUserChemicalsToBeAdded];
  };

  // remove pending custom chemical from list
  const removeChemicalFromList = (rows = [], chemicalData = {}) => {
    const targetTempChemicalId = chemicalData?.tempChemicalId;

    return (Array.isArray(rows) ? rows : []).filter((item) => {
      const isSameByTempChemicalId =
        Boolean(targetTempChemicalId) &&
        item?.tempChemicalId === targetTempChemicalId;

      return !isSameByTempChemicalId;
    });
  };

  const confirmDialogConfig = isMakeDefaultConfirmation
    ? {
        header: t("makeDefault.confirmHeader"),
        description: t("makeDefault.chemicalConfirmDescription"),
        confirmBtn: t("makeDefault.confirmBtn"),
        cancelBtn: t("ValueStrings.cancel"),
      }
    : {
        header: t(`${scope}.confirmHeader`),
        description: t(`${scope}.confirmDescription`),
        confirmBtn: t("ValueStrings.save&Update"),
        cancelBtn: t("ValueStrings.dontSave"),
      };

  const upsertPendingChemicalData = (chemicalData) => {
    setPendingChemicalData((prev) => {
      const prevList = Array.isArray(prev) ? prev.filter(Boolean) : [];
      const hasChemId = Boolean(chemicalData?.chem_id);
      const hasTempChemId = Boolean(chemicalData?.tempChemicalId);

      const index = prevList.findIndex((item) => {
        if (hasChemId) {
          return item?.chem_id === chemicalData?.chem_id;
        }

        if (hasTempChemId) {
          return item?.tempChemicalId === chemicalData?.tempChemicalId;
        }

        return false;
      });

      if (index === -1) {
        return [...prevList, chemicalData];
      }

      const mergedData = [...prevList];
      mergedData[index] = { ...mergedData[index], ...chemicalData };
      return mergedData;
    });
  };

  const openAddEditDialog = (chemicalData) => {
    if (chemicalData) {
      upsertPendingChemicalData(chemicalData);
      setCurrentAction("editChemical");
    } else {
      setPendingChemicalData([]);
      setCurrentAction("addChemical");
    }

    setShowAddEditDialog(true);
    setHasUnsavedChanges(false);
    setHasError(false);
    setShouldResetData(false);
  };

  const closeAddEditDialog = () => {
    setShowAddEditDialog(false);
    setCurrentAction(null);
    setPendingChemicalData([]);
    setHasUnsavedChanges(false);
  };

  // Refer to ChemicalVisibilityToggleRenderer for details
  // const updateChemicalVisibilityLocally = (chemicalData, inheritToPlants) => {
  //   const targetId = chemicalData?.id;
  //   const targetChemId = chemicalData?.chem_id;
  //
  //   setChemicalLibrary((prev) =>
  //     (Array.isArray(prev) ? prev : []).map((item) => {
  //       const isSameRow =
  //         (targetId !== undefined && item?.id === targetId) ||
  //         (targetChemId !== undefined && item?.chem_id === targetChemId);
  //
  //       return isSameRow
  //         ? { ...item, inherit_to_plants: inheritToPlants }
  //         : item;
  //     }),
  //   );
  // };

  // Shared refresh for add/edit/delete to keep plant-level chemical library in sync.
  const refreshPlantChemicalLibraryStore = async () => {
    // Refresh plant chemical library only when current page has plant context.
    if (!getPlantIdFromCurrentUrl()) {
      return;
    }

    const refreshedPlantChemicalLibrary = await fetchChemicalLibraryData(
      t,
      false,
    );

    if (!Array.isArray(refreshedPlantChemicalLibrary)) {
      return;
    }

    const mergedPlantChemicalLibrary = mergeWithPendingChemicals(
      refreshedPlantChemicalLibrary,
    );
    const sortedPlantChemicalLibrary = sortDefaultFirst(
      mergedPlantChemicalLibrary,
    );
    dispatch(storePlantChemicalLibrary(sortedPlantChemicalLibrary));
  };

  const onActionBtnClick = async (actionData) => {
    const { actionType, chemicalData } = actionData;

    //  // Refer to ChemicalVisibilityToggleRenderer for details
    // if (actionType === "toggle-visibility" && isUserScope) {
    //   const targetChemical = (
    //     Array.isArray(chemicalLibrary) ? chemicalLibrary : []
    //   ).find(
    //     (item) =>
    //       item?.id === chemicalData?.id ||
    //       item?.chem_id === chemicalData?.chem_id,
    //   );
    //
    //   const previousVisibility = targetChemical?.inherit_to_plants !== false;
    //   const nextVisibility = chemicalData?.inherit_to_plants !== false;
    //
    //   updateChemicalVisibilityLocally(chemicalData, nextVisibility);
    //
    //   if (!nextVisibility) {
    //     setPendingToggleChemical({
    //       ...chemicalData,
    //       __previousInheritToPlants: previousVisibility,
    //     });
    //     setShowToggleWarning(true);
    //   } else {
    //     applyChemicalVisibilityUpdate(chemicalData);
    //   }
    //   return;
    // }

    if (actionType === "edit-chemical-btn") {
      const isUserCustomChemical =
        isUserScope &&
        Boolean(chemicalData?.is_custom) &&
        chemicalData?.chem_id;

      if (isUserCustomChemical) {
        setIsLoading(true);
        setLoadingText(t("chemicalLibrary.checkingUsageInPlants"));
        try {
          const usageList = await checkUserChemicalUsage(chemicalData.chem_id);
          if (Array.isArray(usageList) && usageList.length > 0) {
            setPendingEditChemical(chemicalData);
            setShowEditUsageWarning(true);
            return;
          }
        } finally {
          setIsLoading(false);
          setLoadingText("");
        }
      }

      openAddEditDialog(chemicalData);
    } else if (actionType === "delete-chemical-btn") {
      const isUserCustomChemical =
        isUserScope && Boolean(chemicalData?.is_custom);

      if (!isUserCustomChemical) {
        return;
      }

      // Remove pending custom chemical from list
      const isPendingCustomChemical =
        !chemicalData?.chem_id && Boolean(chemicalData?.tempChemicalId);

      if (isPendingCustomChemical) {
        const nextChemicalLibrary = sortDefaultFirst(
          removeChemicalFromList(chemicalLibrary, chemicalData),
        );
        setChemicalLibrary(nextChemicalLibrary);
        dispatch(storeUserChemicalLibrary(nextChemicalLibrary));

        const updatedPendingChemicals = removeChemicalFromList(
          getChemicalsFromSession(PENDING_USER_CHEMICALS_TO_BE_ADDED),
          chemicalData,
        );
        saveItemSession(
          PENDING_USER_CHEMICALS_TO_BE_ADDED,
          JSON.stringify(updatedPendingChemicals),
        );

        const updatedPlantChemicalLibrary = sortDefaultFirst(
          removeChemicalFromList(plantChemicalLibrary, chemicalData),
        );
        dispatch(storePlantChemicalLibrary(updatedPlantChemicalLibrary));

        createNotification(
          SUCCESS_NOTIFICATION,
          t("chemicalLibrary.chemicalDeletedSuccessfully"),
          "",
        );
        return;
      }

      setIsLoading(true);
      setLoadingText(t("chemicalLibrary.checkingUsageInPlants"));
      try {
        const usageList = await checkChemicalUsageData(chemicalData.chem_id, t);

        if (!Array.isArray(usageList)) {
          return;
        }

        if (usageList.length > 0) {
          setPendingDeleteUsageList(usageList);
          setPendingDeleteChemical(chemicalData);
          setCurrentAction("deleteChemical");
          return;
        }

        const isDeleted = await deleteChemicalLibraryData(
          chemicalData.chem_id,
          t,
        );
        if (!isDeleted) {
          return;
        }

        const refreshedChemicalLibrary = await fetchChemicalLibraryData(
          t,
          true,
        );
        if (!Array.isArray(refreshedChemicalLibrary)) {
          return;
        }

        const mergedChemicalLibrary = mergeWithPendingChemicals(
          refreshedChemicalLibrary,
        );
        const sortedChemicalLibrary = sortDefaultFirst(mergedChemicalLibrary);
        setChemicalLibrary(sortedChemicalLibrary);
        dispatch(storeUserChemicalLibrary(sortedChemicalLibrary));

        await refreshPlantChemicalLibraryStore();
      } finally {
        setIsLoading(false);
        setLoadingText("");
      }
    }
  };

  const handleDeleteFlowComplete = () => {
    setPendingDeleteChemical(null);
    setPendingDeleteUsageList([]);
    setCurrentAction(null);
  };

  const handleChemicalDeletedFromUsage = async (chemId) => {
    if (!chemId) {
      return;
    }

    const refreshedChemicalLibrary = await fetchChemicalLibraryData(t, true);
    if (!Array.isArray(refreshedChemicalLibrary)) {
      return;
    }

    const mergedChemicalLibrary = mergeWithPendingChemicals(
      refreshedChemicalLibrary,
    );
    const sortedChemicalLibrary = sortDefaultFirst(mergedChemicalLibrary);
    setChemicalLibrary(sortedChemicalLibrary);
    dispatch(storeUserChemicalLibrary(sortedChemicalLibrary));
  };

  // Refer to ChemicalVisibilityToggleRenderer for details
  // const applyChemicalVisibilityUpdate = async (chemicalData) => {
  //   if (!chemicalData || !isUserScope) {
  //     return;
  //   }
  //
  //   const visibilityUpdatePayload = {
  //     ...chemicalData,
  //     inherit_to_plants:
  //       chemicalData?.inherit_to_plants === false ? false : true,
  //   };
  //
  //   setIsLoading(true);
  //   setLoadingText("");
  //   try {
  //     const updatedChemicalLibrary = await updateChemicalLibraryData(
  //       [visibilityUpdatePayload],
  //       t,
  //       true,
  //       false,
  //     );
  //
  //     const nextChemicalLibrary =
  //       Array.isArray(updatedChemicalLibrary) &&
  //       updatedChemicalLibrary.length > 0
  //         ? updatedChemicalLibrary
  //         : await fetchChemicalLibraryData(t, true);
  //
  //     if (!Array.isArray(nextChemicalLibrary)) {
  //       return;
  //     }
  //
  //     const mergedChemicalLibrary =
  //       mergeWithPendingChemicals(nextChemicalLibrary);
  //     const sortedChemicalLibrary = sortDefaultFirst(mergedChemicalLibrary);
  //     setChemicalLibrary(sortedChemicalLibrary);
  //     dispatch(storeUserChemicalLibrary(sortedChemicalLibrary));
  //   } finally {
  //     setIsLoading(false);
  //     setPendingToggleChemical(null);
  //   }
  // };

  // Refer to ChemicalVisibilityToggleRenderer for details
  // const handleToggleWarningClose = (action) => {
  //   setShowToggleWarning(false);
  //
  //   if (action === CustomModalButtons.CONFIRM && pendingToggleChemical) {
  //     applyChemicalVisibilityUpdate(pendingToggleChemical);
  //     return;
  //   }
  //
  //   if (pendingToggleChemical) {
  //     const previousVisibility =
  //       pendingToggleChemical?.__previousInheritToPlants !== false;
  //     updateChemicalVisibilityLocally(
  //       pendingToggleChemical,
  //       previousVisibility,
  //     );
  //   }
  //
  //   setPendingToggleChemical(null);
  // };

  const handleEditUsageWarningClose = (action) => {
    setShowEditUsageWarning(false);

    if (action === CustomModalButtons.CONFIRM && pendingEditChemical) {
      openAddEditDialog(pendingEditChemical);
    }

    setPendingEditChemical(null);
  };

  const saveNewChemicalInUserScope = async (newData) => {
    setIsLoading(true);
    setLoadingText("");
    try {
      const updatedChemicalLibrary = await updateChemicalLibraryData(
        [newData],
        t,
        true,
        false,
      );

      if (!updatedChemicalLibrary) {
        return;
      }

      const refreshedChemicalLibrary = await fetchChemicalLibraryData(t, true);

      if (
        !Array.isArray(refreshedChemicalLibrary) ||
        refreshedChemicalLibrary.length === 0
      ) {
        return;
      }

      const mergedChemicalLibrary = mergeWithPendingChemicals(
        refreshedChemicalLibrary,
      );
      const sortedChemicalLibrary = sortDefaultFirst(mergedChemicalLibrary);
      setChemicalLibrary(sortedChemicalLibrary);
      dispatch(storeUserChemicalLibrary(sortedChemicalLibrary));
      await refreshPlantChemicalLibraryStore();
      closeAddEditDialog();
    } finally {
      setIsLoading(false);
    }
  };

  const handleChemicalEditSubmit = (newData, hasError) => {
    const hasChemicalId = Boolean(newData?.chem_id);
    const hasTempChemId = Boolean(newData?.tempChemicalId);

    if (!isUserScope && (hasChemicalId || hasTempChemId)) {
      upsertPendingChemicalData(newData);
    } else {
      setPendingChemicalData([newData]);
    }

    if (isUserScope && !hasChemicalId && !hasTempChemId) {
      saveNewChemicalInUserScope(newData);
      return;
    }

    if (isUserScope) {
      setShowConfirmation(true);
    }
    setHasUnsavedChanges(true);
    setShouldResetData(false);
    setHasError(hasError);
  };

  const handleChemicalListDialogClose = ({ target: { id } }) => {
    if (id === CustomModalButtons.CONFIRM) {
      if (isUserScope) {
        openAddEditDialog(null);
        return;
      }
      setShowConfirmation(true);
    } else {
      setPendingChemicalData([]);
      onClose();
    }
  };

  const handleConfirmDlgClose = async (action) => {
    setShowConfirmation(false);

    if (action === CustomModalButtons.CONFIRM) {
      const chemicalDataForUpdate = isMakeDefaultConfirmation
        ? chemicalLibrary
        : pendingChemicalData;
      const isUserChemicalPutUpdate =
        isUserScope &&
        !isMakeDefaultConfirmation &&
        Array.isArray(chemicalDataForUpdate) &&
        chemicalDataForUpdate.some((item) => Boolean(item?.chem_id));

      setIsLoading(true);
      setLoadingText(
        isUserChemicalPutUpdate ? t("chemicalLibrary.updatingChemical") : "",
      );
      try {
        let updatedChemicalLibrary = [];

        const isPendingCustomChemicalChanges = chemicalDataForUpdate.every(
          (item) => Boolean(!item?.chem_id) && Boolean(item?.tempChemicalId),
        );

        if (isLocalChanges) {
          const chemicalPayload = chemicalDataForUpdate.map((item) => ({
            chem_id: item.chem_id,
            price: Number(item.price),
            unit: item.unit,
            display_name: item.display_name,
            tempChemicalId: item.tempChemicalId,
          }));

          saveItemSession(
            PENDING_PLANT_CHEMICAL_LIBRARY_KEY,
            JSON.stringify({ chemicals: chemicalPayload }),
          );

          createNotification(
            SUCCESS_NOTIFICATION,
            t("chemicalLibrary.dataUpdatedSuccessfully"),
            "",
          );

          updatedChemicalLibrary = mergeChangedChemicalRows(
            chemicalLibrary,
            chemicalDataForUpdate,
          );
        } else if (isPendingCustomChemicalChanges) {
          if (
            hasDuplicateSymbolAndConcentration(
              chemicalLibrary,
              chemicalDataForUpdate,
            )
          ) {
            createNotification(
              ERROR_NOTIFICATION,
              t("chemicalLibrary.duplicateEntryError"),
              "",
            );
            return;
          }

          updatedChemicalLibrary = mergeChangedChemicalRows(
            chemicalLibrary,
            chemicalDataForUpdate,
          );

          const updatedPendingChemicals = mergeChangedChemicalRows(
            getChemicalsFromSession(PENDING_USER_CHEMICALS_TO_BE_ADDED),
            chemicalDataForUpdate,
          );

          // Save the updated pending chemicals to session storage for user scope.
          if (isUserScope) {
            saveItemSession(
              PENDING_USER_CHEMICALS_TO_BE_ADDED,
              JSON.stringify(updatedPendingChemicals),
            );

            // Remove pending custom chemical from plant scope pending chemicals if it exists there, since user scope takes precedence.
            const editedChem = chemicalDataForUpdate?.[0];
            const pendingPlantChemicals =
              JSON.parse(
                getItemSession(PENDING_PLANT_CHEMICAL_LIBRARY_KEY) || "{}",
              )?.chemicals || [];

            const nextPendingPlantChemicals = pendingPlantChemicals.filter(
              (pendingPlantChem) =>
                !(
                  editedChem?.tempChemicalId &&
                  editedChem.tempChemicalId === pendingPlantChem?.tempChemicalId
                ),
            );

            saveItemSession(
              PENDING_PLANT_CHEMICAL_LIBRARY_KEY,
              JSON.stringify({ chemicals: nextPendingPlantChemicals }),
            );
          } else {
            // Save the updated pending chemicals to session storage for plant scope.
            saveItemSession(
              PENDING_PLANT_CHEMICAL_LIBRARY_KEY,
              JSON.stringify({
                chemicals: updatedPendingChemicals,
              }),
            );
          }

          createNotification(
            SUCCESS_NOTIFICATION,
            t("chemicalLibrary.dataUpdatedSuccessfully"),
            "",
          );

          if (isUserScope) {
            const updatedPlantChemicalLibrary = mergeChangedChemicalRows(
              plantChemicalLibrary,
              chemicalDataForUpdate,
            );
            dispatch(storePlantChemicalLibrary(updatedPlantChemicalLibrary));
          }
        } else {
          // if user edit pending custom chemicals + existing chemicals in a single go
          const { filteredChemicalDataForUpdate, pendingCustomChemicals } =
            chemicalDataForUpdate.reduce(
              (acc, item) => {
                if (!item?.chem_id && item?.tempChemicalId) {
                  acc.pendingCustomChemicals.push(item);
                } else {
                  acc.filteredChemicalDataForUpdate.push(item);
                }
                return acc;
              },
              {
                filteredChemicalDataForUpdate: [],
                pendingCustomChemicals: [],
              },
            );

          if (pendingCustomChemicals.length > 0) {
            saveItemSession(
              PENDING_PLANT_CHEMICAL_LIBRARY_KEY,
              JSON.stringify({ chemicals: pendingCustomChemicals }),
            );
          }
          updatedChemicalLibrary = await updateChemicalLibraryData(
            filteredChemicalDataForUpdate,
            t,
            isUserScope,
            isMakeDefaultConfirmation,
          );
        }

        let nextChemicalLibrary = updatedChemicalLibrary;
        if (isUserChemicalPutUpdate) {
          const refreshedChemicalLibrary = await fetchChemicalLibraryData(
            t,
            true,
          );
          if (
            Array.isArray(refreshedChemicalLibrary) &&
            refreshedChemicalLibrary.length > 0
          ) {
            nextChemicalLibrary = mergeWithPendingChemicals(
              refreshedChemicalLibrary,
            );
          }
        }

        if (
          !Array.isArray(nextChemicalLibrary) ||
          nextChemicalLibrary.length === 0
        ) {
          return;
        }

        const sortedChemicalLibrary = sortDefaultFirst(
          nextChemicalLibrary || [],
        );
        setChemicalLibrary(sortedChemicalLibrary);

        if (isUserScope || isMakeDefaultConfirmation) {
          if (!isUserChemicalPutUpdate) {
            closeAddEditDialog();
          }
          dispatch(storeUserChemicalLibrary(sortedChemicalLibrary));

          if (isUserChemicalPutUpdate) {
            await refreshPlantChemicalLibraryStore();
          }
        } else {
          dispatch(storePlantChemicalLibrary(sortedChemicalLibrary));
          setHasUnsavedChanges(false);
        }
      } finally {
        setIsLoading(false);
        if (isUserChemicalPutUpdate) {
          // Redirect to main dialog only after PUT response (success or failure).
          closeAddEditDialog();
        }
      }
    } else {
      setHasUnsavedChanges(false);
      setShouldResetData(true);
      if (!isUserScope) {
        setPendingChemicalData([]);
      }
    }
  };

  useEffect(() => {
    const loadChemicalLibrary = async () => {
      setIsLoading(true);
      setLoadingText("");
      const chemicalData = await fetchChemicalLibraryData(t, isUserScope);
      const mergedChemicalData = mergeWithPendingChemicals(chemicalData);
      const sortedChemicalData = sortDefaultFirst(mergedChemicalData);
      if (isUserScope) {
        dispatch(storeUserChemicalLibrary(sortedChemicalData));
      } else {
        dispatch(storePlantChemicalLibrary(sortedChemicalData));
      }
      setChemicalLibrary(sortedChemicalData || []);
      setIsLoading(false);
    };

    if (isUserScope && userChemicalLibrary && userChemicalLibrary.length > 0) {
      setChemicalLibrary(sortDefaultFirst(userChemicalLibrary));
    } else if (
      !isUserScope &&
      plantChemicalLibrary &&
      plantChemicalLibrary.length > 0
    ) {
      setChemicalLibrary(sortDefaultFirst(plantChemicalLibrary));
    } else {
      loadChemicalLibrary();
    }
  }, [t, isUserScope]);

  const isConfirmButtonDisabled =
    !isUserScope && (!hasUnsavedChanges || hasError);

  // Refer to ChemicalVisibilityToggleRenderer for details
  // const turnOffChemicalDescription = `<ul class="turn-off-popup m-0 ps-3"><li>${t(
  //   "chemicalLibrary.turnOffChemicalDescription1",
  // )}</li><li>${t("chemicalLibrary.turnOffChemicalDescription2")}</li></ul>`;

  return (
    <>
      {!currentAction && (
        <CustomModal
          header={t(`${scope}.chemicalHeader`)}
          size="lg"
          isLoading={isLoading}
          loadingText={loadingText}
          confirmBtn={
            isUserScope ? (
              <span className="new-badge-corner-wrapper">
                <span>{t("chemicalLibrary.addChemical")}</span>
                <NewBadgeIcon className="new-badge" />
              </span>
            ) : (
              t("ValueStrings.saveChanges")
            )
          }
          onModalClose={handleChemicalListDialogClose}
          confirmBtnDisabled={isConfirmButtonDisabled}
          modalFooter={!isUserScope ? makeDefaultBtn : null}
          customclass="chemical-lib-dialog"
        >
          <WPMessageBanner
            type={BANNER_TYPES.info}
            message={t(`${scope}.infoMessage`)}
            className="marginTop0"
          />

          <ChemicalLibraryTable
            chemicalLibrary={filteredAndSortedChemLibrary}
            context={{ onActionBtnClick }}
            scope={scope}
            isLoading={isLoading}
            onSubmit={handleChemicalEditSubmit}
            shouldResetData={shouldResetData}
          />
        </CustomModal>
      )}
      {showAddEditDialog && (
        <ChemicalLibraryAddEditDialog
          onClose={closeAddEditDialog}
          chemicalRowData={pendingChemicalData[0]}
          chemicalLibrary={chemicalLibrary}
          mode={currentAction === "addChemical" ? "add" : "edit"}
          scope={scope}
          isLoading={isLoading}
          onSubmit={handleChemicalEditSubmit}
          shouldResetData={shouldResetData}
        />
      )}
      {showConfirmation && (
        <ConfirmationPopup
          type={DIALOG_TYPES.WARNING}
          header={confirmDialogConfig.header}
          description={confirmDialogConfig.description}
          confirmBtn={confirmDialogConfig.confirmBtn}
          cancelBtn={confirmDialogConfig.cancelBtn}
          onClose={handleConfirmDlgClose}
        />
      )}
      <ChemicalLibraryDeleteDialog
        chemicalData={pendingDeleteChemical}
        usageList={pendingDeleteUsageList}
        scope={scope}
        onChemicalDeleted={handleChemicalDeletedFromUsage}
        onClose={handleDeleteFlowComplete}
      />
      {/* Refer to ChemicalVisibilityToggleRenderer for details.
      {showToggleWarning && (
        <ConfirmationPopup
          type={DIALOG_TYPES.WARNING}
          header={t("chemicalLibrary.turnOffChemicalWarning")}
          description={turnOffChemicalDescription}
          confirmBtn={t("chemicalLibrary.turnOffBtn")}
          cancelBtn={t("ValueStrings.no")}
          onClose={handleToggleWarningClose}
        />
      )}
      */}
      {showEditUsageWarning && (
        <ConfirmationPopup
          type={DIALOG_TYPES.WARNING}
          header={t("chemicalLibrary.editUsageWarningHeader")}
          description={t("chemicalLibrary.editUsageWarningDescription")}
          confirmBtn={t("ValueStrings.yes")}
          cancelBtn={t("ValueStrings.cancel")}
          onClose={handleEditUsageWarningClose}
        />
      )}
    </>
  );
};

export default ChemicalLibraryDialog;
